import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
    TooltipComponent,
    GridComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';

import type {
    GridComponentOption,
    BarSeriesOption,
    LineSeriesOption,
    TooltipComponentOption,
    LegendComponentOption,
    YAXisComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | LineSeriesOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | YAXisComponentOption
>;

echarts.use([
    TooltipComponent,
    GridComponent,
    BarChart,
    LineChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

export interface ActiveUserTrendPoint {
    period: string;
    period_start: string;
    mau: number;
    avg_dau: number;
    dau_mau_ratio_percent: number;
    is_partial_month?: boolean;
}

interface ActiveUserTrendChartProps {
    data?: ActiveUserTrendPoint[];
}

const ActiveUserTrendChart = ({ data }: ActiveUserTrendChartProps) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (!chartRef.current) {
            return;
        }

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const categories: string[] = [];
        const mauValues: number[] = [];
        const avgDauValues: number[] = [];
        const stickinessValues: number[] = [];

        data.forEach((item) => {
            let label = item.period || '';
            try {
                if (item.period_start) {
                    const date = parseISO(item.period_start.slice(0, 10));
                    label = format(date, 'MMM yyyy');
                    if (item.is_partial_month) {
                        label += ' (MTD)';
                    }
                }
            } catch {
                if (item.is_partial_month) {
                    label += ' (MTD)';
                }
            }
            categories.push(label);
            mauValues.push(Number(item.mau) || 0);
            avgDauValues.push(Number(item.avg_dau) || 0);
            stickinessValues.push(Number(item.dau_mau_ratio_percent) || 0);
        });

        const option: ECOption = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: unknown) => {
                    const items = Array.isArray(params) ? params : [params];
                    const idx = (items[0] as { dataIndex?: number })?.dataIndex ?? 0;
                    const row = data[idx];
                    const partial = row?.is_partial_month ? ' (month to date)' : '';
                    return [
                        `${categories[idx]}${partial}`,
                        `MAU: ${mauValues[idx].toLocaleString()}`,
                        `Avg DAU: ${avgDauValues[idx].toLocaleString()}`,
                        `DAU / MAU: ${stickinessValues[idx].toFixed(1)}%`,
                    ].join('<br/>');
                },
            },
            legend: {
                data: ['MAU', 'Avg DAU', 'DAU / MAU'],
                bottom: 0,
            },
            grid: {
                left: '8%',
                right: '8%',
                bottom: '18%',
                top: '12%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: { fontSize: 10 },
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Users',
                    position: 'left',
                },
                {
                    type: 'value',
                    name: 'Stickiness %',
                    position: 'right',
                    axisLabel: { formatter: '{value}%' },
                    splitLine: { show: false },
                },
            ],
            series: [
                {
                    name: 'MAU',
                    type: 'bar',
                    data: mauValues,
                    itemStyle: { color: '#1890ff' },
                },
                {
                    name: 'Avg DAU',
                    type: 'line',
                    data: avgDauValues,
                    smooth: true,
                    itemStyle: { color: '#52c41a' },
                },
                {
                    name: 'DAU / MAU',
                    type: 'line',
                    yAxisIndex: 1,
                    data: stickinessValues,
                    smooth: true,
                    itemStyle: { color: '#fa8c16' },
                },
            ],
        };

        chartInstance.current.setOption(option, true);

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [data]);

    React.useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default ActiveUserTrendChart;
